<app-popup
  [heading]="'Email Events - ' + businessName"
  [showCloseIcon]="true"
  [showFooterButtons]="false"
  (cancelClicked)="closePopup()"
>
  <div class="email-events-content">
    @if (loading) {
      <div class="state-container loading-container">
        <div class="spinner"></div>
        <p>Loading email events...</p>
      </div>
    } @else if (errorMessage) {
      <div class="state-container error-container">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="#ef4444"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <p>{{ errorMessage }}</p>
        <button class="retry-button" (click)="loadEmailEvents()">Retry</button>
      </div>
    } @else if (emailData) {
      <section class="summary-section">
        <div class="email-info-grid">
          <div class="info-card info-card-wide">
            <span class="info-label">Recipient Email</span>
            <div class="info-value">{{ emailData.recipient_email }}</div>
          </div>
          <div class="info-card">
            <span class="info-label">Email Triggered On</span>
            <div class="info-value">
              {{ emailData.email_sent_at_utc | date: "medium" }}
            </div>
          </div>
          @if (emailData.form_submitted_at_utc) {
            <div class="info-card">
              <span class="info-label">Form Submitted</span>
              <div class="info-value">
                {{ emailData.form_submitted_at_utc | date: "medium" }}
              </div>
            </div>
          }
          @if (emailData.last_reminded_at_utc) {
            <div class="info-card">
              <span class="info-label">Last Reminder</span>
              <div class="info-value">
                {{ emailData.last_reminded_at_utc | date: "medium" }}
              </div>
            </div>
          }
        </div>
      </section>

      <section class="events-section">
        <div class="events-header">
          <h3>Email Activity Timeline</h3>
          <span class="event-count-pill">
            {{ emailData.events?.length ?? 0 }}
            {{ (emailData.events?.length ?? 0) === 1 ? "Event" : "Events" }}
          </span>
        </div>
        @if (emailData.events && emailData.events.length > 0) {
          <div
            class="timeline"
            role="list"
            aria-label="Email activity timeline"
          >
            @for (event of reversedEvents; track $index) {
              <article class="timeline-item" role="listitem">
                <div
                  class="timeline-marker"
                  [ngClass]="getStatusClass(event.status)"
                ></div>
                <div class="timeline-content">
                  <div class="event-title">
                    {{ formatStatus(event.status) }}
                  </div>
                  <div class="event-subtitle">
                    {{ event.time | date: "medium" }}
                  </div>

                  @if (event.link) {
                    <div class="event-extra-info">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.047 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4061 4.45791 20.4791 3.53087C19.5521 2.60383 18.298 2.07799 16.987 2.0666C15.676 2.0552 14.413 2.55918 13.47 3.46997L11.75 5.17997"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M14 11C13.5705 10.4259 13.0226 9.95083 12.3934 9.60707C11.7643 9.26331 11.0685 9.05889 10.3533 9.00768C9.63816 8.95647 8.92037 9.05967 8.24861 9.31023C7.57685 9.56079 6.96689 9.95303 6.45996 10.46L3.45996 13.46C2.54917 14.403 2.04519 15.666 2.05659 16.977C2.06798 18.288 2.59382 19.5421 3.52086 20.4691C4.4479 21.3961 5.70197 21.922 7.01295 21.9334C8.32393 21.9448 9.58694 21.4408 10.53 20.53L12.24 18.82"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <a
                        [href]="event.link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="event-link"
                        [title]="event.link"
                      >
                        {{ event.link }}
                      </a>
                    </div>
                  }

                  @if (event.failure_reason) {
                    <div class="event-extra-info failure-reason">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <span>{{ event.failure_reason }}</span>
                    </div>
                  }
                </div>
              </article>
            }
          </div>
        } @else {
          <div class="no-events">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                stroke="#94a3b8"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <p>No email events recorded yet</p>
          </div>
        }
      </section>
    }
  </div>
</app-popup>
